import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { ProfileDropdown } from '../components/ProfileDropdown';
import { PenaltyCard } from '../components/PenaltyCard';
import { RotateCw, X } from 'lucide-react';
import {
  getEventsList,
  getHyroxPortSheet,
  updateHyroxPortSheet,
  getPenaltyImage,
  loadLocations,
  getUserCurrentEventId,
  type HyroxPortSheetData,
} from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import type { Location } from '../lib/supabase';

export function ReviewPenalty() {
  return (
    <ProtectedRoute>
      <ReviewPenaltyContent />
    </ProtectedRoute>
  );
}

const BATCH_SIZE = 20;
const AUTO_REFRESH_MS = 30000;
const DUPLICATE_WARNING_WINDOW_MS = 90 * 60 * 1000;

/** Same rules as Timing Dashboard: second+ Warning for same race # + station within 90 min → Needs Review. */
function flagDuplicateWarningsAsNeedsReview(data: HyroxPortSheetData[]): {
  updatedData: HyroxPortSheetData[];
  modifiedIds: Set<number>;
} {
  const updatedData = [...data];
  const modifiedIds = new Set<number>();

  const penaltiesByRaceAndStation = new Map<string, HyroxPortSheetData[]>();

  updatedData.forEach((penalty) => {
    if (penalty.status === 'Warning') {
      const key = `${penalty.racenumber}-${penalty.station}`;
      if (!penaltiesByRaceAndStation.has(key)) {
        penaltiesByRaceAndStation.set(key, []);
      }
      penaltiesByRaceAndStation.get(key)!.push(penalty);
    }
  });

  penaltiesByRaceAndStation.forEach((penalties) => {
    if (penalties.length < 2) return;

    const sortedPenalties = [...penalties].sort(
      (a, b) => new Date(a.approved_at).getTime() - new Date(b.approved_at).getTime()
    );

    for (let i = 1; i < sortedPenalties.length; i++) {
      const currentPenalty = sortedPenalties[i];
      const currentTime = new Date(currentPenalty.approved_at).getTime();

      for (let j = 0; j < i; j++) {
        const previousPenalty = sortedPenalties[j];
        const previousTime = new Date(previousPenalty.approved_at).getTime();
        const timeDifference = currentTime - previousTime;

        if (timeDifference <= DUPLICATE_WARNING_WINDOW_MS && previousPenalty.status === 'Warning') {
          const index = updatedData.findIndex((p) => p.penlogid === currentPenalty.penlogid);
          if (index !== -1) {
            updatedData[index] = { ...updatedData[index], status: 'Needs Review' };
            modifiedIds.add(currentPenalty.penlogid);
          }
          break;
        }
      }
    }
  });

  return { updatedData, modifiedIds };
}

function ReviewPenaltyContent() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [eventName, setEventName] = useState<string>('');
  const [isLoadingEvent, setIsLoadingEvent] = useState(true);

  const [penaltyData, setPenaltyData] = useState<HyroxPortSheetData[]>([]);
  const [isLoadingPenalties, setIsLoadingPenalties] = useState(false);

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [stationFilter, setStationFilter] = useState<string>('all');
  const [locations, setLocations] = useState<Location[]>([]);
  const [showStationDropdown, setShowStationDropdown] = useState(false);
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [imageRotation, setImageRotation] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const feedContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledDown = useRef(false);

  // Fetch user's current event, locations, and penalty types on mount
  useEffect(() => {
    (async () => {
      setIsLoadingEvent(true);
      const [eventsResult, locationsResult, eventIdResult] = await Promise.all([
        getEventsList(),
        loadLocations(),
        user?.email ? getUserCurrentEventId(user.email) : Promise.resolve({ success: false } as { success: false }),
      ]);
      if (eventIdResult.success && eventIdResult.data?.currenteventid && eventsResult.success) {
        const eid = eventIdResult.data.currenteventid;
        setSelectedEventId(eid);
        const matched = eventsResult.events.find((e) => e.eventid === eid);
        setEventName(matched?.eventname || `Event #${eid}`);
      }
      if (locationsResult.success && locationsResult.data) {
        setLocations(locationsResult.data);
      }
      setIsLoadingEvent(false);
    })();
  }, [user?.email]);

  // Fetch penalties when event changes
  const fetchPenalties = useCallback(async () => {
    if (!selectedEventId) return;
    setIsLoadingPenalties(true);
    const result = await getHyroxPortSheet(selectedEventId);
    if (result.success) {
      const { updatedData, modifiedIds } = flagDuplicateWarningsAsNeedsReview(result.penaltyData);
      for (const penlogid of modifiedIds) {
        const saveResult = await updateHyroxPortSheet({ penlogid, status: 'Needs Review' });
        if (!saveResult.success) {
          console.error(`Failed to persist Needs Review for penlogid ${penlogid}:`, saveResult.error);
        }
      }
      setPenaltyData(updatedData);
    }
    setIsLoadingPenalties(false);
  }, [selectedEventId]);

  useEffect(() => {
    if (selectedEventId) {
      setVisibleCount(BATCH_SIZE);
      fetchPenalties();
    }
  }, [selectedEventId, fetchPenalties]);

  // Auto-refresh
  useEffect(() => {
    if (!selectedEventId) return;
    const interval = setInterval(fetchPenalties, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [selectedEventId, fetchPenalties]);

  // Sorted & filtered data
  const filteredData = useMemo(() => {
    let data = [...penaltyData];
    if (statusFilter !== 'all') {
      data = data.filter((p) => p.status === statusFilter);
    }
    if (stationFilter !== 'all') {
      data = data.filter((p) => p.station === stationFilter);
    }
    return data.sort(
      (a, b) => new Date(b.approved_at || 0).getTime() - new Date(a.approved_at || 0).getTime()
    );
  }, [penaltyData, statusFilter, stationFilter]);

  const visibleData = useMemo(() => filteredData.slice(0, visibleCount), [filteredData, visibleCount]);
  const hasMore = visibleCount < filteredData.length;

  // Intersection observer for lazy loading
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, filteredData.length));
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, filteredData.length]);

  // Pull-to-refresh: refresh when user scrolls back to top after scrolling down
  useEffect(() => {
    const container = feedContainerRef.current;
    if (!container || !selectedEventId) return;
    const onScroll = () => {
      if (container.scrollTop > 200) {
        hasScrolledDown.current = true;
      }
      if (container.scrollTop === 0 && hasScrolledDown.current && !isLoadingPenalties) {
        hasScrolledDown.current = false;
        fetchPenalties();
      }
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [selectedEventId, fetchPenalties, isLoadingPenalties]);

  // Status counts
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: penaltyData.length };
    penaltyData.forEach((p) => {
      counts[p.status] = (counts[p.status] || 0) + 1;
    });
    return counts;
  }, [penaltyData]);

  // Approve / Reject handlers
  const handleApprove = async (penlogid: number) => {
    await updateHyroxPortSheet({ penlogid, status: 'Approved' });
    setPenaltyData((prev) =>
      prev.map((p) => (p.penlogid === penlogid ? { ...p, status: 'Approved' } : p))
    );
  };

  const handleReject = async (penlogid: number) => {
    await updateHyroxPortSheet({ penlogid, status: 'Rejected' });
    setPenaltyData((prev) =>
      prev.map((p) => (p.penlogid === penlogid ? { ...p, status: 'Rejected' } : p))
    );
  };

  // Photo modal handlers
  const handlePhotoClick = async (penlogid: number) => {
    setIsLoadingImage(true);
    setShowImageModal(true);
    setImageRotation(0);
    setImageZoom(1);
    try {
      const result = await getPenaltyImage(penlogid);
      setSelectedImage(result.success && result.athracephotourl ? result.athracephotourl : null);
    } catch {
      setSelectedImage(null);
    } finally {
      setIsLoadingImage(false);
    }
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
    setIsLoadingImage(false);
    setImageRotation(0);
    setImageZoom(1);
  };

  const chipBase =
    'bg-sw-panel border border-sw-border rounded-sm px-1 py-2 text-center text-[9px] font-extrabold tracking-[0.06em] uppercase text-sw-muted transition-colors';
  const chipOn = 'bg-sw-yellow border-sw-yellow text-black [&_.chip-c]:text-black';
  const chipHot = '[&_.chip-c]:text-sw-red';

  return (
    <div className="pen-screen h-screen flex flex-col overflow-hidden">
      <header className="pen-topbar flex-shrink-0">
        <Link to="/dashboard" className="block hover:opacity-80 transition-opacity">
          <img
            src="/HyroxLogo.png"
            alt="Hyrox Logo"
            className="h-9 w-auto object-contain"
          />
        </Link>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => navigate('/head-judge-dashboard')}
            className="bg-black border border-sw-border rounded-sm px-2.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.03em] text-sw-yellow hover:border-sw-yellow"
          >
            ⌂ Log Penalty
          </button>
          <ProfileDropdown variant="pen" />
        </div>
      </header>

      <div className="flex-shrink-0 px-4 pt-4 pb-2 space-y-3">
        <div className="min-w-0 space-y-1">
          <div className="text-xs font-black tracking-[0.02em] text-white uppercase">
            Review Queue
          </div>
          {eventName && (
            <div className="text-2xl font-black tracking-[0.01em] text-white leading-tight">
              {eventName}
            </div>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2">
          {([
            { key: 'Needs Review', label: 'Pending', hot: true },
            { key: 'Approved', label: 'Approved', hot: false },
            { key: 'all', label: 'All', hot: false },
          ] as const).map(({ key, label, hot }) => {
            const count = statusCounts[key] || 0;
            const isActive = statusFilter === key;
            return (
              <button
                key={key}
                onClick={() => { setStatusFilter(key); setVisibleCount(BATCH_SIZE); }}
                className={`${chipBase} ${isActive ? chipOn : ''} ${hot && !isActive ? chipHot : ''}`}
                title={key === 'all' ? 'All' : key}
              >
                <span className="chip-c block font-mono font-light text-base text-white mb-0.5">{count}</span>
                {label}
              </button>
            );
          })}

          <div className="relative">
            <button
              onClick={() => setShowStationDropdown(!showStationDropdown)}
              className={`${chipBase} w-full ${stationFilter !== 'all' ? chipOn : ''}`}
              title={stationFilter === 'all' ? 'Filter by Workstation' : stationFilter}
            >
              <span className="chip-c block font-mono font-light text-base text-white mb-0.5">·</span>
              Station
            </button>
            {showStationDropdown && (
              <div className="absolute top-full right-0 mt-1 bg-sw-panel border border-sw-border rounded-sm shadow-lg z-20 w-48 max-h-60 overflow-y-auto">
                <button
                  onClick={() => { setStationFilter('all'); setShowStationDropdown(false); setVisibleCount(BATCH_SIZE); }}
                  className={`w-full text-left px-3 py-2 text-xs font-medium text-white hover:bg-black ${stationFilter === 'all' ? 'text-sw-yellow font-bold' : ''}`}
                >
                  All Workstations
                </button>
                {locations.map((loc) => (
                  <button
                    key={loc.locationid}
                    onClick={() => { setStationFilter(loc.locationname || ''); setShowStationDropdown(false); setVisibleCount(BATCH_SIZE); }}
                    className={`w-full text-left px-3 py-2 text-xs font-medium text-white hover:bg-black ${stationFilter === loc.locationname ? 'text-sw-yellow font-bold' : ''}`}
                  >
                    {loc.locationname}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 w-full max-w-[420px] mx-auto">
          <div className="text-[11px] text-sw-muted text-left min-w-0">
            {selectedEventId
              ? `Showing ${visibleData.length} of ${filteredData.length}`
              : '—'}
          </div>
          <button
            type="button"
            onClick={fetchPenalties}
            disabled={isLoadingPenalties || !selectedEventId}
            className="pen-btn-ghost !min-h-8 !px-3 !py-1.5 !text-[10px] flex-shrink-0 disabled:opacity-50"
            title="Refresh"
          >
            <RotateCw className={`w-3.5 h-3.5 ${isLoadingPenalties ? 'animate-spin' : ''}`} />
            
          </button>
        </div>
      </div>

      <div ref={feedContainerRef} className="flex-1 overflow-y-auto px-4 py-3">
        {isLoadingEvent ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sw-yellow" />
              <p className="text-sw-muted text-xs uppercase tracking-wide font-extrabold">Loading event...</p>
            </div>
          </div>
        ) : !selectedEventId ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sw-muted">No event assigned to your account</p>
          </div>
        ) : isLoadingPenalties && penaltyData.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sw-yellow" />
              <p className="text-sw-muted text-xs uppercase tracking-wide font-extrabold">Loading penalty records...</p>
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sw-muted">
              {penaltyData.length === 0
                ? 'No penalty records found for this event'
                : `No ${statusFilter} records found`}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-6">
            {isLoadingPenalties && penaltyData.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-sw-muted">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sw-yellow" />
                Refreshing...
              </div>
            )}

            {visibleData.map((penalty) => (
              <PenaltyCard
                key={penalty.penlogid}
                penalty={penalty}
                onApprove={handleApprove}
                onReject={handleReject}
                onPhotoClick={handlePhotoClick}
              />
            ))}

            {hasMore && (
              <div ref={sentinelRef} className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sw-yellow" />
              </div>
            )}
          </div>
        )}
      </div>

      {showImageModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-sw-panel border border-sw-yellow rounded-sm p-4 max-w-6xl max-h-[95vh] w-full relative font-brutal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-black uppercase tracking-wide text-white">Penalty Photo</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setImageRotation((r) => (r + 90) % 360)}
                  className="p-2 rounded-sm border border-sw-border hover:border-sw-yellow transition-colors"
                >
                  <RotateCw className="w-5 h-5 text-white" />
                </button>
                <button
                  onClick={closeImageModal}
                  className="p-2 rounded-sm border border-sw-border hover:border-sw-yellow transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
            <div className="flex justify-center">
              {isLoadingImage ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sw-yellow mb-4" />
                  <p className="text-sw-muted text-xs uppercase tracking-wide">Loading image...</p>
                </div>
              ) : selectedImage ? (
                <img
                  src={selectedImage}
                  alt="Penalty Photo"
                  className="max-w-full max-h-[70vh] object-contain rounded-sm transition-transform duration-300"
                  style={{ transform: `rotate(${imageRotation}deg) scale(${imageZoom})`, transformOrigin: 'center' }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-sw-muted">No image available for this penalty</p>
                </div>
              )}
            </div>
            <div className="mt-4 text-center">
              <button onClick={closeImageModal} className="pen-btn-ghost min-w-[48%]">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
